
import { useState, useCallback } from 'react';
import { ComparisonStatus, RepairEstimate, ComparisonResult, SectionStatistics } from '@/types/quick-compare';
import { 
  compareEstimates, 
  formatLineItemsForDisplay, 
  groupDifferencesBySection,
  calculateSectionStatistics 
} from '@/utils/estimates/compareEstimates';
import { toast } from '@/hooks/use-toast';

/**
 * Custom hook for managing the estimate comparison workflow
 */
export function useEstimateComparison() {
  const [status, setStatus] = useState<ComparisonStatus>('idle');
  const [originalEstimate, setOriginalEstimate] = useState<RepairEstimate | null>(null);
  const [modifiedEstimate, setModifiedEstimate] = useState<RepairEstimate | null>(null);
  const [comparisonResults, setComparisonResults] = useState<ComparisonResult | null>(null);
  const [sectionStats, setSectionStats] = useState<Record<string, SectionStatistics>>({});
  const [groupedDifferences, setGroupedDifferences] = useState<Record<string, any>>({});

  /**
   * Handle file processing callback
   */
  const handleFileProcessed = useCallback((estimate: RepairEstimate, type: 'original' | 'modified') => {
    if (type === 'original') {
      setOriginalEstimate(estimate);
    } else {
      setModifiedEstimate(estimate);
    }
    
    toast(`Successfully processed ${type} estimate`);
  }, []);

  /**
   * Start the comparison process
   */
  const startComparison = useCallback(async () => {
    if (!originalEstimate || !modifiedEstimate) {
      toast("Please upload both the original and modified estimates", {
        style: { backgroundColor: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))' }
      });
      return;
    }

    try {
      setStatus('comparing');
      
      // Use deep-diff to compare the estimates
      const results = compareEstimates(originalEstimate, modifiedEstimate);
      
      // Process the results for display
      const groupedBySection = groupDifferencesBySection(results.differences);
      const sectionStatistics = calculateSectionStatistics(groupedBySection);
      
      // Add artificial delay for better UX
      setTimeout(() => {
        setComparisonResults(results);
        setGroupedDifferences(groupedBySection);
        setSectionStats(sectionStatistics);
        setStatus('complete');
        
        // Show a toast with the results summary
        if (results.isIdentical) {
          toast("The estimates are identical. No differences were found.");
        } else {
          toast(`Found ${results.differences.length} differences between the estimates.`);
        }
      }, 1500);
    } catch (error) {
      console.error("Error during comparison:", error);
      setStatus('error');
      toast("There was an error comparing the estimates", {
        style: { backgroundColor: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))' }
      });
    }
  }, [originalEstimate, modifiedEstimate]);

  /**
   * Reset the comparison state
   */
  const resetComparison = useCallback(() => {
    setStatus('idle');
    setOriginalEstimate(null);
    setModifiedEstimate(null);
    setComparisonResults(null);
    setSectionStats({});
    setGroupedDifferences({});
  }, []);

  return {
    status,
    setStatus,
    originalEstimate,
    modifiedEstimate,
    comparisonResults,
    groupedDifferences,
    sectionStats,
    handleFileProcessed,
    startComparison,
    resetComparison,
  };
}
